import type { IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const CHIP_SIZE = {
  sm: 'h-6 px-3 text-xs leading-4',
  md: 'h-8 px-4 text-sm leading-5',
  lg: 'h-10 px-5 text-base leading-6',
} as const;

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

interface ChipBaseProps {
  /** The surface the chip sits on, not a meaning: `neutral` on the page, `secondary` on a card. @default 'neutral' */
  tone?: 'neutral' | 'secondary';
  /** The pill's box, with the type moving with it. A pressable `kind` keeps the 44px floor regardless. @default 'md' */
  size?: 'sm' | 'md' | 'lg';
  /** Leading icon (a topic glyph). */
  icon?: IconName;
  children: ReactNode;
  className?: string;
}

export interface ChipLabelProps extends ChipBaseProps {
  /**
   * What the chip does — decides the element AND the tap target. `label` is a
   * span at its `size` height (it is text, not a target); `link` and `filter`
   * are 44px, the house floor for anything pressable.
   * @default 'label'
   */
  kind?: 'label';
}

export interface ChipLinkProps extends ChipBaseProps {
  kind: 'link';
  /** Destination for `kind="link"` — a listing filtered by this term. */
  href: string;
  /** Called when a `kind="link"` chip is activated. */
  onClick?: () => void;
}

export interface ChipFilterProps extends ChipBaseProps {
  kind: 'filter';
  /** Called when a `kind="filter"` chip is pressed; the chip renders its own remove control. */
  onRemove?: () => void;
  /** Screen-reader text for a filter chip's remove control. Defaults to "Remove filter". */
  srLabel?: string;
}

/** Discriminated on `kind` — `link` requires `href`, so an anchor can never render without one. */
export type ChipProps = ChipLabelProps | ChipLinkProps | ChipFilterProps;

/**
 * Chip — one pill for the whole system: the topic, benefit, dietary or
 * applied-filter term that sits under a card, beside a title, or above a set
 * of results. `Badge` is not a chip — it stays for short status/count markers.
 */
export function Chip(props: ChipProps) {
  const { kind = 'label', tone = 'neutral', size = 'md', icon, children, className } = props;
  const { t } = useTranslation('shared');
  const pressable = kind !== 'label';
  const base = cn(
    'inline-flex shrink-0 items-center gap-2 rounded-full border font-medium text-foreground',
    pressable ? 'min-h-11 px-4 text-sm' : CHIP_SIZE[size],
    tone === 'secondary' ? 'bg-secondary' : 'bg-card',
    pressable && 'transition-colors duration-fast ease-standard',
    pressable &&
      (tone === 'secondary'
        ? 'hover:bg-[color-mix(in_oklab,var(--secondary)_88%,var(--foreground))]'
        : 'hover:bg-accent'),
    pressable && FOCUS,
    className,
  );
  const glyph = icon ? <Icon name={icon} size="xs" className="shrink-0 text-muted-foreground" /> : null;

  if (props.kind === 'filter') {
    const { onRemove, srLabel } = props;
    return (
      <button type="button" onClick={onRemove} className={base}>
        {glyph}
        {children}
        <Icon name="x" size="xs" className="shrink-0 text-muted-foreground" />
        <span className="sr-only">{srLabel || t('common.removeFilter')}</span>
      </button>
    );
  }
  if (props.kind === 'link') {
    const { href, onClick } = props;
    return (
      <a href={href} onClick={onClick} className={base}>
        {glyph}
        {children}
      </a>
    );
  }
  return (
    <span className={base}>
      {glyph}
      {children}
    </span>
  );
}
